"use client";

import { Image as ImageType } from "@/types/dto/common/image";
import { UnforgettableMoments } from "@/types/dto/destinations-page/destination-page-types";
import { Tour } from "@/types/dto/tours/tour";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { useTranslations } from "next-intl";
import { SectionHeader } from "@/components/common/section-header";
import sanitizeHtml from "@/utils/sanitize-html";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import {
  isDarkLandingPageBackground,
  resolveLandingPageSectionBackgroundColor
} from "@/utils/landing-page-background";

export interface UnforgettableMomentsProps {
  moments: UnforgettableMoments;
  moduleRankingPosition?: string;
  containerSx?: SxProps<Theme>;
  headerGap?: SxProps;
  backgroundColor?: string;
}
interface MiniCardProps {
  title: string;
  image: ImageType;
  copy: string;
  tour?: Tour;
  index: number;
}

const MiniCard = ({ title, image, copy, tour, index }: MiniCardProps) => {
  const t = useTranslations("SmallGroupExplorationsPage");
  const hasCta = Boolean(tour?.days && tour?.tourUrl && tour.tourUrl.length > 0);

  const handleDays = (days: string | number): string => {
    const daysStr = String(days);
    const parts = daysStr.split("-");
    return parts[1] ?? daysStr;
  };

  return (
    <Box
      data-testid={`moments-card-${index}`}
      data-track={hasCta ? "click" : undefined}
      data-tour-name={tour?.title}
      data-tour-price={tour?.price ? tour.price.toString().replace(/[^0-9]/g, "") : undefined}
      data-tour-duration={tour?.days ? String(tour.days) : undefined}
      data-tour-id={(tour as any)?.neoId}
      data-carousel-position={String(index + 1)}
      sx={{
        height: "100%"
      }}
    >
      <Card
        component="div"
        sx={{
          borderRadius: "2rem",
          boxShadow: 6,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          textDecoration: "none",
          overflow: "hidden",
          backgroundColor: "background.default",
          alignItems: "stretch",
          position: "relative"
        }}
      >
        {hasCta && (
          <Link
            data-testid={`moments-card-link-${index}`}
            href={tour!.tourUrl}
            tabIndex={-1}
            style={{
              zIndex: 5,
              alignItems: "center",
              display: "flex"
            }}
            className="pseudo-absolute-full-container"
          />
        )}
        {image?.imageUrl && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 218
            }}
          >
            <Image
              src={image.imageUrl || ""}
              alt={image.altText || "Tour image"}
              fill
              style={{ objectFit: "cover" }}
              data-testid={`moments-card-image-${index}`} // Unique test ID for the image
            />
          </Box>
        )}

        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: hasCta ? "space-between" : "flex-start",
            border: "1px solid",
            borderColor: "divider",
            borderTop: "none",
            borderBottomLeftRadius: 2,
            borderBottomRightRadius: 2,
            padding: 2.5,
            borderRadius: "0 0 2rem 2rem"
          }}
        >
          <Box sx={{ mb: 1 }} component="div">
            {title && (
              <Typography
                variant="h5"
                component="h3"
                data-testid={`moments-card-title-${index}`} // Unique test ID for the title
                sx={{
                  color: "text.primary",
                  mb: 2,
                  minHeight: { xs: 48, lg: 70, xl: 48 },
                  lineHeight: "normal",
                  display: "flex",
                  alignItems: "flex-start"
                }}
              >
                {title}
              </Typography>
            )}
            {copy && (
              <Typography
                component="div"
                variant="subtitle1"
                sx={{
                  color: "text.primary",
                  fontWeight: "500",
                  // Responsive typography styles based on Figma specs
                  fontSize: { xs: "1.6rem", md: "1.8rem" }
                }}
              >
                {typeof copy === "string" ? (
                  // eslint-disable-next-line react/no-danger
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(copy) }}
                    className="moments-card-content"
                    data-testid={`moments-card-copy-${index}`}
                  />
                ) : (
                  copy
                )}
              </Typography>
            )}
          </Box>
          {hasCta && (
            <ColletteButton
              component="a"
              href={tour!.tourUrl}
              variant="contained"
              color="primary"
              size="large"
              testId={`moments-card-button-${index}`} // Unique test ID for the button
              label={t("viewItinerary", { days: handleDays(tour!.days) })}
              sx={{
                textAlign: "center",
                zIndex: 8
              }}
            />
          )}
        </CardContent>

      </Card>
    </Box>
  );
};

export const UnforgettableMomentsComponent: React.FC<UnforgettableMomentsProps> = ({
  moments,
  headerGap = { paddingBottom: "2.5rem" },
  moduleRankingPosition = "9",
  containerSx = {},
  backgroundColor
}) => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const moduleBackgroundColor = backgroundColor ?? moments.backgroundColor;
  const resolvedBackgroundColor = resolveLandingPageSectionBackgroundColor(moduleBackgroundColor, "transparent");
  const isDarkBackground = isDarkLandingPageBackground(moduleBackgroundColor);

  return (
    <Box
      id="moments"
      className="anchor-section"
      data-track="visibility"
      data-parent-container-type="cards"
      data-module-name="Unforgettable Moments Section"
      data-ranking-algorithm="manual"
      data-module-ranking="custom_sort"
      data-module-ranking-position={moduleRankingPosition}
      component="section"
      sx={{
        backgroundColor: resolvedBackgroundColor,
        ...containerSx
      }}
    >
      <Stack 
        className="destination-page-container"
        sx={{ p: { xs: "0 2.4rem", lg: "0 10rem" } }}
      >
        <SectionHeader
          headerText={moments.basicInfo.headerText}
          headerSx={{
            color: isDarkBackground ? "primary.contrastText" : "primary.main",
            fontSize: { xs: "2.4rem", sm: "2.6rem", md: "3.2rem" },
            textAlign: "center"
          }}
          copySx={{
            textAlign: "center",
            color: isDarkBackground ? "primary.contrastText" : "text.primary"
          }}
          copyText={moments.basicInfo.copyText || ""}
          headerTestId="unforgettable-moments-header"
          copyTestId="unforgettable-moments-copy"
          sx={headerGap}
        />
        <Grid container spacing={3} alignItems="stretch">
          {moments.tourCards.map((card, index) => {
            const totalCards = moments.tourCards.length;

            // Dynamically assign column width
            let mdCols;

            if (totalCards === 1) {
              mdCols = 12;
            } else if (totalCards === 2) {
              mdCols = 6;
            } else if (totalCards === 3) {
              mdCols = isLgUp ? 4 : 6; // 3 cards at ≥1200px, else show 2 cards/row
            } else {
              mdCols = isLgUp ? 3 : 6; // 4+ cards: 3+ per row at ≥1200px, 2 per row otherwise
            }

            return (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: mdCols
                }}
                key={card.tour?.id ?? index}
              >
                <MiniCard
                  title={card.title}
                  image={card.image}
                  copy={card.copy}
                  tour={card.tour}
                  index={index}
                />
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </Box>
  );
};
